import { EditorDevice } from "./types";

export interface DeviceChange {
  uuid: string;
  displayName: string;
  /** For a move, the site it is leaving. */
  fromTrapSiteName: string | null;
}

export interface DeviceDiff {
  /** Newly attached and not currently on any site. */
  added: DeviceChange[];
  /** Newly attached but currently on a different site - a move. */
  moved: DeviceChange[];
  /** Detached; becomes unassigned. */
  removed: DeviceChange[];
}

export const isEmptyDiff = (diff: DeviceDiff): boolean =>
  diff.added.length === 0 &&
  diff.moved.length === 0 &&
  diff.removed.length === 0;

/**
 * What Save will actually do to device assignments.
 *
 * A device belongs to exactly one site, so attaching one that already has a
 * home is a move, not an addition - and the confirm dialog has to say so by
 * name, because the other site silently loses it.
 *
 * `editingId` is null while creating: nothing can be leaving a site that does
 * not exist yet, so every attachment is an add or a move.
 */
export const diffDeviceAssignments = (
  initialIds: string[],
  nextIds: string[],
  devices: EditorDevice[],
  editingId: string | null,
): DeviceDiff => {
  const byUuid = new Map(devices.map((d) => [d.uuid, d]));
  const before = new Set(initialIds);
  const after = new Set(nextIds);

  const describe = (uuid: string): DeviceChange => {
    const device = byUuid.get(uuid);
    return {
      uuid,
      displayName: device?.displayName || uuid,
      fromTrapSiteName: device?.trapSiteName ?? null,
    };
  };

  const added: DeviceChange[] = [];
  const moved: DeviceChange[] = [];

  for (const uuid of nextIds) {
    if (before.has(uuid)) continue;
    const device = byUuid.get(uuid);
    // Already somewhere else, and somewhere else that is not this site.
    const isMove = !!device?.trapSiteId && device.trapSiteId !== editingId;
    (isMove ? moved : added).push(describe(uuid));
  }

  const removed = initialIds
    .filter((uuid) => !after.has(uuid))
    // A removal describes where it is going, not where it came from.
    .map((uuid) => ({ ...describe(uuid), fromTrapSiteName: null }));

  return { added, moved, removed };
};
